import { useState } from 'react';
import { router, Link } from '@inertiajs/react';
import GmLayout from '@/layouts/GmLayout';

/**
 * GM Tome Book Create page - form for creating a new book.
 */
export default function Create() {
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [hue, setHue] = useState(30); // Default amber/brown
    const [isPublished, setIsPublished] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errors, setErrors] = useState<Record<string, string>>({});

    // Handle form submission
    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);
        setErrors({});

        router.post('/gm/tome/books', {
            title,
            description: description || null,
            hue,
            is_published: isPublished,
        }, {
            onError: (errors) => {
                setErrors(errors);
                setIsSubmitting(false);
            },
            onSuccess: () => {
                setIsSubmitting(false);
            },
        });
    };

    // Generate preview color from hue
    const accentColor = `hsl(${hue}, 60%, 40%)`;

    return (
        <GmLayout title="Create Book" activePath="/gm/tome/books">
            <div className="max-w-2xl">
                <div className="flex items-center gap-4 mb-6">
                    <Link
                        href="/gm/tome/books"
                        className="text-gray-400 hover:text-white transition-colors"
                    >
                        ← Back to Books
                    </Link>
                </div>

                <h1 className="text-2xl font-bold text-white mb-6">📚 Create New Book</h1>

                <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Title */}
                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                            Title <span className="text-red-400">*</span>
                        </label>
                        <input
                            type="text"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded text-white placeholder-gray-400 focus:border-purple-500 focus:ring-1 focus:ring-purple-500"
                            placeholder="Enter book title"
                            required
                        />
                        {errors.title && <p className="mt-1 text-sm text-red-400">{errors.title}</p>}
                    </div>

                    {/* Description */}
                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                            Description
                        </label>
                        <textarea
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            rows={4}
                            className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded text-white placeholder-gray-400 focus:border-purple-500 focus:ring-1 focus:ring-purple-500"
                            placeholder="Optional description for the book"
                        />
                        {errors.description && <p className="mt-1 text-sm text-red-400">{errors.description}</p>}
                    </div>

                    {/* Hue Color Picker */}
                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                            Accent Color (Hue: {hue}°)
                        </label>
                        <div className="flex items-center gap-4">
                            <input
                                type="range"
                                min="0"
                                max="360"
                                value={hue}
                                onChange={(e) => setHue(parseInt(e.target.value))}
                                className="flex-1 h-2 rounded-lg appearance-none cursor-pointer"
                                style={{
                                    background: 'linear-gradient(to right, hsl(0,60%,50%), hsl(60,60%,50%), hsl(120,60%,50%), hsl(180,60%,50%), hsl(240,60%,50%), hsl(300,60%,50%), hsl(360,60%,50%))',
                                }}
                            />
                            <div
                                className="w-12 h-12 rounded border-2 border-slate-600"
                                style={{ backgroundColor: accentColor }}
                            />
                        </div>
                        {errors.hue && <p className="mt-1 text-sm text-red-400">{errors.hue}</p>}
                    </div>

                    {/* Published Toggle */}
                    <div className="flex items-center gap-3">
                        <input
                            type="checkbox"
                            id="is_published"
                            checked={isPublished}
                            onChange={(e) => setIsPublished(e.target.checked)}
                            className="w-5 h-5 rounded border-slate-600 bg-slate-700 text-purple-600 focus:ring-purple-500"
                        />
                        <label htmlFor="is_published" className="text-gray-300">
                            Publish immediately
                        </label>
                    </div>

                    {/* Submit Button */}
                    <div className="flex gap-4">
                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className="px-6 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-purple-800 disabled:cursor-not-allowed text-white rounded transition-colors"
                        >
                            {isSubmitting ? 'Creating...' : 'Create Book'}
                        </button>
                        <Link
                            href="/gm/tome/books"
                            className="px-6 py-2 bg-slate-600 hover:bg-slate-500 text-white rounded transition-colors"
                        >
                            Cancel
                        </Link>
                    </div>
                </form>
            </div>
        </GmLayout>
    );
}

